import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import TriSource from "components/Actions/dialogs/components/TriSource/TriSource";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import AlertContext from "context/alert/alertContext";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import TriSourceContext from "context/triSource/triSourceContext";
import {
    BackgroundActionData,
    ENDPOINTS,
    TABLE_NAMES,
    TRI_SOURCES,
    useApi,
    useMediaQueries
} from "shared";

import { useTokenValidation } from "../../hooks";
import {
    DeviceTransferModalProps,
    TRANSFER_DEVICE_WINDOWS,
    TransferDevicesValidationProps
} from "../../types";
import TransferDevicesContent from "./TransferDevicesContent";

const TransferDevices = ({
    isOpen,
    modalCSS,
    close,
    changeDialogWindow,
    removeResize
}: DeviceTransferModalProps &
    Omit<TransferDevicesValidationProps, "token">) => {
    const { setBackgroundActionId } = useContext(AlertContext);
    const { setReloadItems } = useContext(ParamsContext);

    const { activeSource, sourcePayload, isSourceReady } =
        useContext(TriSourceContext);

    const { deselectAllRows } = useContext(TableContext);

    const { t } = useTranslation();
    const { toMd } = useMediaQueries();
    const { postData, handleResponse } = useApi();

    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [token, setToken] = useState("");

    const {
        tokenError,
        isTokenLoading,
        validationData,
        validateToken,
        clearTokenError,
        clearValidationData
    } = useTokenValidation({ token, removeResize });

    const isReady =
        isSourceReady &&
        sourcePayload &&
        token.trim().length > 0 &&
        !tokenError;

    const handleToken = (data: string) => setToken(data);

    const getFormData = () => {
        const formData = new FormData();

        formData.append("source", activeSource);
        formData.append("file", sourcePayload as Blob);
        formData.append("data[token]", token);

        return formData;
    };

    const getPayloadData = () => {
        return {
            source: activeSource,
            [activeSource]: sourcePayload,
            data: { token }
        };
    };

    const deselectAndClose = () => {
        deselectAllRows();
        close();
    };

    const submitTransferDevices = async () => {
        try {
            setIsSubmitLoading(true);

            const payloadData =
                activeSource === TRI_SOURCES.FromFile
                    ? getFormData()
                    : getPayloadData();

            const { data }: { data: BackgroundActionData } = await postData(
                `${ENDPOINTS.Devices}/transfer`,
                payloadData
            );

            setBackgroundActionId(
                data,
                TABLE_NAMES.Devices,
                () => {
                    deselectAndClose();
                    setReloadItems(TABLE_NAMES.Devices);
                },
                () => setIsSubmitLoading(false)
            );
        } catch (error) {
            handleResponse(error);
            setIsSubmitLoading(false);
        }
    };

    const goToMainDialog = () => {
        changeDialogWindow(TRANSFER_DEVICE_WINDOWS.Main);
        removeResize(false);
    };

    return (
        <TriSource
            title={t("Dialog##transfer devices")}
            description={t(
                validationData
                    ? "Dialog##device transfer##transfer devices##confirmation"
                    : "Dialog##device transfer##transfer devices##device token"
            )}
            confirmationDescription={t(
                "Dialog##confirmations##transfer devices confirmation"
            )}
            hideTriSource={!!validationData}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={close}
            isBulkActionLoading={isSubmitLoading}
            isLoading={isSubmitLoading}
            hasIgnoreConfirmation={!validationData}
            submit={validationData ? submitTransferDevices : validateToken}
            actions={
                <>
                    <Button
                        fullWidth
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Normal}
                        variant={BUTTON_VARIANTS.TextOnly}
                        onClick={
                            validationData
                                ? clearValidationData
                                : goToMainDialog
                        }
                        disabled={isTokenLoading || isSubmitLoading}
                    >
                        {t("Button##back")}
                    </Button>

                    <Button
                        data-testid={
                            validationData
                                ? undefined
                                : "transfer-devices-next-button"
                        }
                        fullWidth
                        color={BUTTON_COLORS.Primary}
                        size={BUTTON_SIZES.Normal}
                        variant={BUTTON_VARIANTS.TextOnly}
                        type="submit"
                        isLoading={
                            validationData ? isSubmitLoading : isTokenLoading
                        }
                        disabled={validationData ? false : !isReady}
                        loaderText={
                            validationData
                                ? t("Button##transferring")
                                : undefined
                        }
                    >
                        {validationData
                            ? t("Button##transfer")
                            : t("Button##next")}
                    </Button>
                </>
            }
            slotProps={{
                transition: {
                    onExited: () => {
                        setIsSubmitLoading(false);
                        goToMainDialog();
                    }
                }
            }}
            css={modalCSS}
            extendToMaxWidth={toMd}
        >
            <TransferDevicesContent
                tokenError={tokenError}
                tokenCompany={validationData}
                isLoading={isSubmitLoading}
                setPayload={handleToken}
                clearError={clearTokenError}
            />
        </TriSource>
    );
};
export default TransferDevices;
